import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/use-toast.js';
import { useTranslation } from 'react-i18next';
import { resolveBackendBase } from '../utils/backendBase.js';
import { loginRequest } from '../utils/authToken.js';
import { establishSession } from '../utils/sessionSetup.js';
import { normalizePin, PIN_LENGTH } from '../utils/pinPolicy.js';
const TRUSTED_DEVICE_KEY = 'trusted_device';
const LAST_USERNAME_KEY = 'last_login_username';
function formatApiDetail(detail) {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(item => item?.msg || '').filter(Boolean).join('، ');
  return detail?.msg || 'تعذر إكمال تسجيل الدخول';
}
function readTrustedDevice() {
  try {
    return JSON.parse(localStorage.getItem(TRUSTED_DEVICE_KEY) || 'null');
  } catch (e) {
    return null;
  }
}
const Login = () => {
  const {
    toast
  } = useToast();
  useTranslation();
  const [initialTrusted] = useState(() => readTrustedDevice());
  const [name, setName] = useState(() => localStorage.getItem(LAST_USERNAME_KEY) || '');
  const [password, setPassword] = useState('');
  const [pin, setPin] = useState('');
  const [showPassword, setShowPassword] = useState(true);
  const [rememberDevice, setRememberDevice] = useState(true);
  const [pinMode, setPinMode] = useState(false);
  const [trusted, setTrusted] = useState(initialTrusted);
  const [loading, setLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  useEffect(() => {
    const t = readTrustedDevice();
    if (t?.device_id && t?.username) {
      setTrusted(t);
      setName(current => current || t.username);
    }
  }, []);
  const warmCaches = async () => {
    const API_URL = `${resolveBackendBase()}/api`;
    const warm = async (path, key, updatedKey) => {
      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 8000);
        const res = await fetch(`${API_URL}${path}`, {
          signal: controller.signal
        });
        clearTimeout(timeout);
        if (!res.ok) return;
        const data = await res.json();
        if (Array.isArray(data) && data.length > 0) {
          localStorage.setItem(key, JSON.stringify(data));
          if (updatedKey) localStorage.setItem(updatedKey, new Date().toISOString());
        }
      } catch (e) {/* ignore warmup failures */}
    };
    await Promise.all([warm('/operations?limit=200', 'operationsCache:all', 'operationsCacheUpdatedAt:all'), warm('/accounts', 'chartAccountsCache:all')]);
  };
  const finishLogin = async data => {
    localStorage.setItem(LAST_USERNAME_KEY, data.username);
    if (data?.device_id && data?.pin_configured === true && rememberDevice) {
      localStorage.setItem(TRUSTED_DEVICE_KEY, JSON.stringify({
        device_id: data.device_id,
        username: data.username,
        user_id: data.user_id
      }));
    } else {
      localStorage.removeItem(TRUSTED_DEVICE_KEY);
    }
    await establishSession({
      username: data.username,
      userId: data.user_id,
      role: data.role,
      permissions: data.permissions,
      token: data.access_token
    });
    toast({
      title: 'مرحباً بك',
      description: `أهلاً بعودتك، ${data.username}`
    });
    void warmCaches();
    window.location.replace(`${window.location.origin}/`);
  };
  const handleLogin = async () => {
    setAuthError('');
    if (!name.trim()) {
      setAuthError('أدخل اسم المستخدم للمتابعة');
      toast({
        title: 'اسم المستخدم مطلوب',
        description: 'أدخل اسم المستخدم للمتابعة',
        variant: 'destructive'
      });
      return;
    }
    const normalizedPin = normalizePin(pin);
    if (pinMode && normalizedPin.length !== PIN_LENGTH) {
      setAuthError('أدخل رمز PIN المكوّن من 6 أرقام');
      toast({
        title: 'رمز غير مكتمل',
        description: 'أدخل رمز PIN المكوّن من 6 أرقام',
        variant: 'destructive'
      });
      return;
    }
    if (!pinMode && !password) {
      setAuthError('أدخل كلمة المرور للمتابعة');
      toast({
        title: 'كلمة المرور مطلوبة',
        description: 'أدخل كلمة المرور الاحتياطية',
        variant: 'destructive'
      });
      return;
    }
    try {
      setLoading(true);
      const body = {
        username: name.trim()
      };
      if (pinMode) {
        body.pin = normalizedPin;
        const matchingTrustedDevice = trusted?.username === name.trim() ? trusted.device_id : null;
        if (matchingTrustedDevice) body.device_id = matchingTrustedDevice;
        body.remember_device = !matchingTrustedDevice;
      } else if (showPassword && password) {
        body.password = password;
        body.remember_device = rememberDevice;
      }
      const res = await loginRequest(body);
      if (!res.ok) {
        if (res.status === 401 && /كلمة المرور مطلوبة/.test(res.detail || '')) {
          setShowPassword(true);
          setPinMode(false);
          setAuthError('كلمة المرور مطلوبة لهذا الحساب');
          toast({
            title: 'كلمة المرور مطلوبة',
            description: 'كلمة المرور مطلوبة لهذا الحساب — أدخلها للمتابعة'
          });
          return;
        }
        if (res.status === 429) {
          setAuthError('محاولات كثيرة — انتظر قليلاً ثم حاول مجددًا');
          toast({
            title: 'محاولات كثيرة',
            description: 'انتظر قليلاً ثم حاول مجدداً',
            variant: 'destructive'
          });
          return;
        }
        if (pinMode && res.status === 401) {
          localStorage.removeItem(TRUSTED_DEVICE_KEY);
          setTrusted(null);
          setPinMode(false);
          setShowPassword(true);
          setPin('');
          setAuthError('تعذر استخدام PIN على هذا الجهاز. أدخل كلمة المرور للمتابعة');
          toast({
            title: 'استخدم كلمة المرور',
            description: 'تعذر استخدام PIN على هذا الجهاز، فتم نقلك إلى الدخول الآمن بكلمة المرور',
            variant: 'destructive'
          });
          return;
        }
        const message = formatApiDetail(res.detail) || 'اسم المستخدم غير معروف أو بيانات الدخول غير صحيحة';
        setAuthError(message);
        toast({
          title: 'خطأ',
          description: message,
          variant: 'destructive'
        });
        return;
      }
      await finishLogin(res.data);
    } catch (e) {
      console.error('Login error:', e);
      setAuthError('فشل في تسجيل الدخول بسبب تعذر الاتصال أو انتهاء المهلة');
      toast({
        title: 'خطأ',
        description: 'فشل في تسجيل الدخول (تعذر الاتصال أو مهلة)',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };
  const handleKeyPress = e => {
    if (e.key === 'Enter') handleLogin();
  };
  return _jsxs("div", {
    className: "min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden",
    children: [_jsx("div", {
      className: "fixed top-[-10%] left-[-10%] w-[500px] h-[500px] bg-blue-200/30 rounded-full blur-[100px] pointer-events-none",
      "aria-hidden": "true"
    }), _jsx("div", {
      className: "fixed bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-indigo-200/30 rounded-full blur-[100px] pointer-events-none",
      "aria-hidden": "true"
    }), _jsxs("div", {
      className: "w-full max-w-[400px] z-10 animate-fade-in",
      children: [_jsxs("div", {
        className: "apple-card p-10 flex flex-col items-center text-center",
        children: [_jsx("div", {
          className: "w-16 h-16 bg-gradient-to-br from-[#0071E3] to-[#00C7BE] rounded-2xl mb-8 shadow-lg flex items-center justify-center text-white text-2xl font-bold",
          children: "W"
        }), _jsx("h1", {
          className: "text-3xl font-bold text-[#1D1D1F] mb-2 tracking-tight",
          children: "\u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644"
        }), _jsx("p", {
          className: "text-[#86868B] text-base mb-8",
          children: "\u0646\u0638\u0627\u0645 \u0625\u062F\u0627\u0631\u0629 \u0627\u0644\u0648\u0631\u0634 \u0627\u0644\u0630\u0643\u064A"
        }), _jsxs("div", {
          className: "w-full space-y-5",
          children: [_jsxs("div", {
            className: "space-y-2 text-right",
            children: [_jsx("label", {
              className: "text-sm font-medium text-[#1D1D1F] mr-1",
              htmlFor: "login-username",
              children: "\u0627\u0633\u0645 \u0627\u0644\u0645\u0633\u062A\u062E\u062F\u0645"
            }), _jsx("input", {
              id: "login-username",
              type: "text",
              placeholder: "\u0645\u062B\u0627\u0644: \u0627\u062D\u0645\u062F",
              value: name,
              onChange: event => {
                setName(event.target.value);
                setAuthError('');
              },
              onKeyPress: handleKeyPress,
              className: "apple-input",
              autoFocus: true,
              autoComplete: "username",
              "data-testid": "login-username-input"
            })]
          }), pinMode && _jsxs("div", {
            className: "space-y-2 text-right",
            children: [_jsx("label", {
              className: "text-sm font-medium text-[#1D1D1F] mr-1",
              children: "\u0631\u0645\u0632 \u0627\u0644\u062F\u062E\u0648\u0644 \u0627\u0644\u0633\u0631\u064A\u0639"
            }), _jsx("input", {
              type: "password",
              inputMode: "numeric",
              maxLength: PIN_LENGTH,
              placeholder: "\u2022\u2022\u2022\u2022\u2022\u2022",
              value: pin,
              onChange: e => {
                setPin(normalizePin(e.target.value).slice(0, PIN_LENGTH));
                setAuthError('');
              },
              onKeyPress: handleKeyPress,
              className: "apple-input tracking-[0.5em] text-center text-xl",
              autoComplete: "off",
              "data-testid": "login-pin-input"
            })]
          }), !pinMode && showPassword && _jsxs(_Fragment, {
            children: [_jsxs("div", {
              className: "space-y-2 text-right",
              children: [_jsx("label", {
                className: "text-sm font-medium text-[#1D1D1F] mr-1",
                children: "\u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631"
              }), _jsx("input", {
                type: "password",
                placeholder: "\u0623\u062F\u062E\u0644 \u0643\u0644\u0645\u0629 \u0627\u0644\u0645\u0631\u0648\u0631",
                value: password,
                onChange: e => {
                  setPassword(e.target.value);
                  setAuthError('');
                },
                onKeyPress: handleKeyPress,
                className: "apple-input",
                autoComplete: "current-password",
                "data-testid": "login-password-input"
              })]
            }), _jsxs("label", {
              className: "flex items-center gap-2 text-sm text-[#1D1D1F] justify-end cursor-pointer select-none",
              children: [_jsx("span", {
                children: "\u062A\u0630\u0643\u0651\u0631 \u0647\u0630\u0627 \u0627\u0644\u062C\u0647\u0627\u0632"
              }), _jsx("input", {
                type: "checkbox",
                checked: rememberDevice,
                onChange: e => setRememberDevice(e.target.checked),
                className: "w-4 h-4 accent-[#0071E3]",
                "data-testid": "login-remember-device-checkbox"
              })]
            })]
          }), authError && _jsx("div", {
            role: "alert",
            "aria-live": "assertive",
            className: "w-full border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700 text-right",
            "data-testid": "login-error-alert",
            children: authError
          }), _jsx("button", {
            onClick: handleLogin,
            disabled: loading,
            className: "apple-button w-full flex items-center justify-center gap-2",
            "data-testid": "login-submit-button",
            children: loading ? _jsx("span", {
              className: "w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"
            }) : 'دخول'
          }), _jsxs("div", {
            className: "flex items-center justify-center text-xs pt-1",
            children: [pinMode || trusted?.device_id ? _jsx("button", {
              type: "button",
              onClick: () => {
                const nextPinMode = !pinMode;
                setPinMode(nextPinMode);
                setShowPassword(!nextPinMode);
                setAuthError('');
              },
              className: "text-[#0071E3] hover:underline",
              "data-testid": "login-pin-mode-toggle",
              children: pinMode ? 'استخدام كلمة المرور الاحتياطية' : 'العودة إلى PIN السريع'
            }) : _jsx("span", {
              className: "text-[#6E6E73]",
              "data-testid": "login-pin-availability-note",
              children: "\u064A\u062A\u0627\u062D PIN \u0627\u0644\u0633\u0631\u064A\u0639 \u0628\u0639\u062F \u062A\u0633\u062C\u064A\u0644 \u0627\u0644\u062F\u062E\u0648\u0644 \u0648\u062A\u0648\u062B\u064A\u0642 \u0647\u0630\u0627 \u0627\u0644\u062C\u0647\u0627\u0632"
            }), !pinMode && !showPassword && _jsx("button", {
              type: "button",
              onClick: () => setShowPassword(true),
              className: "text-[#0071E3] hover:underline",
              "data-testid": "login-show-password-toggle",
              children: "\u0644\u062F\u064A\u0651 \u0643\u0644\u0645\u0629 \u0645\u0631\u0648\u0631"
            }), _jsx("a", {
              href: "/account/setup",
              className: "text-[#0071E3] hover:underline",
              "data-testid": "login-password-recovery-link",
              children: "نسيت كلمة المرور؟"
            })]
          })]
        }), _jsx("div", {
          className: "mt-8 text-sm text-[#86868B]",
          children: "\u0646\u0633\u062E\u0629 \u062A\u062C\u0631\u064A\u0628\u064A\u0629 v2.1"
        })]
      }), _jsx("div", {
        className: "mt-8 text-center text-[#86868B] text-sm",
        children: "\u062C\u0645\u064A\u0639 \u0627\u0644\u062D\u0642\u0648\u0642 \u0645\u062D\u0641\u0648\u0638\u0629 \u00A9 2025"
      })]
    })]
  });
};
export default Login;
